import { useEffect, useMemo, useState } from "react";
import {
  ExternalLink,
  Eye,
  EyeOff,
  FileText,
  Globe2,
  HelpCircle,
  Home,
  Image,
  Inbox,
  LogOut,
  Mail,
  MessageSquare,
  Newspaper,
  Scale,
  Users,
  UserCog,
  Wrench,
} from "lucide-react";
import { buildDefaultContent } from "@/content/defaultContent";
import { deepMerge } from "@/content/ContentContext";
import { apiGetContent, apiSaveContent } from "@/admin/adminApi";
import { setPath } from "@/admin/fields";
import GlobalTab from "@/admin/GlobalTab";
import HomeTab from "@/admin/HomeTab";
import { BlogTab, ContactTab, LegalTab } from "@/admin/ContentTabs";
import ServicesTab from "@/admin/ServicesTab";
import AboutTab from "@/admin/AboutTab";
import LeadsTab from "@/admin/LeadsTab";
import MediaTab from "@/admin/MediaTab";
import AccountTab from "@/admin/AccountTab";
import ChatTab from "@/admin/ChatTab";
import FaqTab from "@/admin/FaqTab";
import PreviewPane from "@/admin/PreviewPane";

// Page each content tab previews by default
const previewPaths = {
  global: "/",
  home: "/",
  services: "/services",
  about: "/about",
  blog: "/blog",
  contact: "/contact",
  legal: "/privacy-policy",
  faqs: "/",
};

const tabs = [
  { id: "global", label: "Global", icon: Globe2 },
  { id: "home", label: "Home Page", icon: Home },
  { id: "services", label: "Services", icon: Wrench },
  { id: "about", label: "About Page", icon: Users },
  { id: "blog", label: "Blog", icon: Newspaper },
  { id: "contact", label: "Contact Page", icon: Mail },
  { id: "legal", label: "Legal Pages", icon: Scale },
  { id: "faqs", label: "FAQs", icon: HelpCircle },
  { id: "chat", label: "Live Chat", icon: MessageSquare },
  { id: "leads", label: "Leads Inbox", icon: Inbox },
  { id: "media", label: "Media", icon: Image },
  { id: "account", label: "Account", icon: UserCog },
];

const AdminPanel = ({ email, onEmailChange, onLogout }) => {
  const defaults = useMemo(() => buildDefaultContent(), []);
  const [content, setContent] = useState(null);
  const [tab, setTab] = useState("global");
  const [preview, setPreview] = useState(false);
  const [previewPath, setPreviewPath] = useState("/");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    apiGetContent()
      .then((res) => setContent(deepMerge(defaults, res.data.content || {})))
      .catch(() => setContent(defaults));
  }, [defaults]);

  const update = (path, value) => {
    setContent((prev) => setPath(prev, path, value));
    setDirty(true);
    setMessage("");
  };

  const selectTab = (id) => {
    setTab(id);
    if (previewPaths[id]) {
      setPreviewPath(previewPaths[id]);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage("");
    try {
      await apiSaveContent(content);
      setDirty(false);
      setMessage("Changes saved. The live site is updated.");
    } catch (err) {
      setMessage("Save failed. Please try again.");
    }
    setSaving(false);
  };

  if (!content) {
    return (
      <div className="admin-loading" data-testid="admin-loading">
        Loading content...
      </div>
    );
  }

  return (
    <div className="admin-panel" data-testid="admin-panel">
      <aside className="admin-sidebar">
        <div className="admin-brand">
          <FileText size={16} />
          <span>
            IT Device Services
            <small>Website Admin</small>
          </span>
        </div>
        <nav className="admin-nav">
          {tabs.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                className={tab === item.id ? "admin-nav-item admin-nav-active" : "admin-nav-item"}
                onClick={() => selectTab(item.id)}
                data-testid={`admin-tab-${item.id}`}
              >
                <Icon size={14} />
                {item.label}
              </button>
            );
          })}
        </nav>
        <div className="admin-sidebar-footer">
          <a className="admin-nav-item" href="/" target="_blank" rel="noopener noreferrer" data-testid="admin-view-site">
            <ExternalLink size={14} />
            View Site
          </a>
          <button type="button" className="admin-nav-item" onClick={onLogout} data-testid="admin-logout-btn">
            <LogOut size={14} />
            Log Out
          </button>
        </div>
      </aside>

      <div className="admin-main">
        <div className="admin-topbar">
          <h1 className="admin-title">{tabs.find((item) => item.id === tab)?.label}</h1>
          {previewPaths[tab] && (
            <div className="admin-topbar-right">
              <button
                type="button"
                className={preview ? "af-btn af-btn-primary" : "af-btn"}
                onClick={() => setPreview((value) => !value)}
                data-testid="admin-preview-toggle"
              >
                {preview ? <EyeOff size={13} /> : <Eye size={13} />}
                {preview ? "Hide preview" : "Live preview"}
              </button>
            </div>
          )}
        </div>

        <div className={preview && previewPaths[tab] ? "admin-body admin-body-split" : "admin-body"}>
        <div className="admin-content">
          {tab === "global" && (
            <GlobalTab content={content} update={update} saving={saving} dirty={dirty} message={message} onSave={handleSave} />
          )}
          {tab === "home" && (
            <HomeTab content={content} update={update} saving={saving} dirty={dirty} message={message} onSave={handleSave} />
          )}
          {tab === "services" && (
            <ServicesTab content={content} update={update} saving={saving} dirty={dirty} message={message} onSave={handleSave} />
          )}
          {tab === "about" && (
            <AboutTab content={content} update={update} saving={saving} dirty={dirty} message={message} onSave={handleSave} />
          )}
          {tab === "blog" && (
            <BlogTab content={content} update={update} saving={saving} dirty={dirty} message={message} onSave={handleSave} />
          )}
          {tab === "contact" && (
            <ContactTab content={content} update={update} saving={saving} dirty={dirty} message={message} onSave={handleSave} />
          )}
          {tab === "legal" && (
            <LegalTab content={content} update={update} saving={saving} dirty={dirty} message={message} onSave={handleSave} />
          )}
          {tab === "faqs" && (
            <FaqTab content={content} update={update} saving={saving} dirty={dirty} message={message} onSave={handleSave} />
          )}
          {tab === "chat" && <ChatTab />}          {tab === "leads" && <LeadsTab />}
          {tab === "media" && <MediaTab />}
          {tab === "account" && <AccountTab email={email} onEmailChange={onEmailChange} />}
        </div>
        {preview && previewPaths[tab] && (
          <PreviewPane content={content} path={previewPath} onPathChange={setPreviewPath} />
        )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
